import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import styles from './Organizations.module.css';

interface OrgFormData {
  name: string;
  slug: string;
  is_active: boolean;
}

export const Organizations: React.FC = () => {
  const { user, token } = useAuth();
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingOrg, setEditingOrg] = useState<any>(null);
  const [expandedOrg, setExpandedOrg] = useState<number | null>(null);
  const [orgUsers, setOrgUsers] = useState<Record<number, any[]>>({});
  const [formData, setFormData] = useState<OrgFormData>({
    name: '',
    slug: '',
    is_active: true,
  });

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/organizations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setOrganizations(data.organizations || []);
    } catch (error) {
      console.error('Error fetching organizations:', error);
      toast.error('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  const handleCreate = () => {
    setEditingOrg(null);
    setFormData({
      name: '',
      slug: '',
      is_active: true,
    });
    setShowModal(true);
  };

  const handleEdit = (org: any) => {
    setEditingOrg(org);
    setFormData({
      name: org.name,
      slug: org.slug,
      is_active: org.is_active === 1,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const url = editingOrg
        ? `${import.meta.env.VITE_API_URL}/api/organizations/${editingOrg.id}`
        : `${import.meta.env.VITE_API_URL}/api/organizations`;
      const response = await fetch(url, {
        method: editingOrg ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(editingOrg ? 'Organization updated' : 'Organization created');
        setShowModal(false);
        fetchOrganizations();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to save organization');
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      toast.error('Failed to save organization');
    }
  };

  const handleDelete = async (id: number) => {
    if (
      !window.confirm(
        'Are you sure you want to delete this organization? This will delete all associated users and data.',
      )
    ) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/organizations/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Organization deleted');
        fetchOrganizations();
      } else {
        const data = await response.json();
        toast.error(data.error || 'Failed to delete organization');
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      toast.error('Failed to delete organization');
    }
  };

  const toggleExpandOrg = async (orgId: number) => {
    if (expandedOrg === orgId) {
      setExpandedOrg(null);
      return;
    }

    setExpandedOrg(orgId);

    // Fetch users for this organization if not already loaded
    if (!orgUsers[orgId]) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/organizations/${orgId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (response.ok && data.data) {
          setOrgUsers((prev) => ({
            ...prev,
            [orgId]: data.data.users || [],
          }));
        }
      } catch (error) {
        console.error('Error fetching organization users:', error);
        toast.error('Failed to load users');
      }
    }
  };

  if (loading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Organizations</h2>
        <button className={styles.button} onClick={handleCreate}>
          + Create Organization
        </button>
      </div>

      <table className={styles.table}>
        <thead className={styles.thead}>
          <tr className={styles.tr}>
            <th className={styles.th}>Name</th>
            <th className={styles.th}>Slug</th>
            <th className={styles.th}>Users</th>
            <th className={styles.th}>Status</th>
            <th className={styles.th}>Created</th>
            <th className={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {organizations.map((org) => (
            <>
              <tr key={org.id} className={styles.tr}>
                <td className={styles.td}>{org.name}</td>
                <td className={styles.td}>{org.slug}</td>
                <td className={styles.td}>
                  <button className={styles.actionButton} onClick={() => toggleExpandOrg(org.id)}>
                    {org.user_count || 0} {expandedOrg === org.id ? '▼' : '▶'}
                  </button>
                </td>
                <td className={styles.td}>
                  <span className={styles.statusBadge} data-active={String(org.is_active === 1)}>
                    {org.is_active === 1 ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className={styles.td}>{new Date(org.created_at).toLocaleDateString()}</td>
                <td className={styles.td}>
                  <button className={styles.actionButton} onClick={() => handleEdit(org)}>
                    Edit
                  </button>
                  <button className={styles.actionButton} onClick={() => handleDelete(org.id)}>
                    Delete
                  </button>
                </td>
              </tr>
              {expandedOrg === org.id && (
                <tr key={`${org.id}-users`} className={styles.tr}>
                  <td className={styles.usersExpandedCell} colSpan={6}>
                    <strong>Users in {org.name}:</strong>
                    {orgUsers[org.id] && orgUsers[org.id].length > 0 ? (
                      <ul className={styles.usersList}>
                        {orgUsers[org.id].map((u) => (
                          <li key={u.id}>
                            {u.username} ({u.email})
                            {u.is_active === 0 && <span className={styles.userInactive}> - Inactive</span>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className={styles.noUsers}>No users in this organization</div>
                    )}
                  </td>
                </tr>
              )}
            </>
          ))}
        </tbody>
      </table>

      {organizations.length === 0 && (
        <div className={styles.emptyState}>No organizations found. Create one to get started.</div>
      )}

      {showModal && (
        <div className={styles.modal} onClick={() => setShowModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>{editingOrg ? 'Edit Organization' : 'Create Organization'}</h3>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Organization Name</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., ACME Corporation"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Slug</label>
                <input
                  className={styles.input}
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  required
                  placeholder="e.g., acme"
                  pattern="[a-z0-9-]+"
                  title="Only lowercase letters, numbers, and hyphens"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <input
                    className={styles.checkbox}
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className={styles.modalActions}>
                <button
                  className={styles.button}
                  type="button"
                  data-variant="secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button className={styles.button} type="submit">
                  {editingOrg ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
